import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ThumbsUp, ThumbsDown, Clock, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { format } from 'date-fns';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface FeedbackItem {
  id: string;
  book_id?: string;
  book_title: string;
  book_author: string;
  feedback_type: 'upvote' | 'downvote' | 'dismissed';
  recommendation_reason?: string;
  created_at: string;
}

export default function RecommendationHistory() {
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'all' | 'upvote' | 'downvote'>('all');

  useEffect(() => {
    fetchFeedback();
  }, []);

  const fetchFeedback = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/auth');
        return;
      }

      const { data, error } = await supabase
        .from('recommendation_feedback')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setFeedback((data || []) as FeedbackItem[]);
    } catch (error) {
      console.error('Error fetching feedback:', error);
      toast.error('Failed to load recommendation history');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFeedback = async (id: string) => {
    try {
      const { error } = await supabase
        .from('recommendation_feedback')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setFeedback(prev => prev.filter(item => item.id !== id));
      toast.success('Feedback removed');
    } catch (error) {
      console.error('Error deleting feedback:', error);
      toast.error('Failed to remove feedback');
    }
  };

  const filteredFeedback = feedback.filter(item => 
    activeTab === 'all' || item.feedback_type === activeTab
  );

  const getFeedbackIcon = (type: string) => {
    switch (type) {
      case 'upvote':
        return <ThumbsUp className="w-4 h-4 text-green-500" />;
      case 'downvote':
      case 'dismissed':
        return <ThumbsDown className="w-4 h-4 text-destructive" />;
      default:
        return null;
    }
  };

  const getFeedbackLabel = (type: string) => {
    switch (type) {
      case 'upvote':
        return 'Added to TBR';
      case 'downvote':
      case 'dismissed':
        return 'Not Interested';
      default:
        return type;
    }
  };

  if (loading) {
    return (
      <div className="min-h-[100dvh] bg-background w-full max-w-full overflow-x-hidden">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center py-12">
            <div className="text-muted-foreground">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-background w-full max-w-full overflow-x-hidden">
      
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">Recommendation History</h1>
          <p className="text-muted-foreground">
            Track all book recommendations you've interacted with and how they've shaped your reading profile.
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="mb-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="all">
              All ({feedback.length})
            </TabsTrigger>
            <TabsTrigger value="upvote">
              Added ({feedback.filter(f => f.feedback_type === 'upvote').length})
            </TabsTrigger>
            <TabsTrigger value="downvote">
              Dismissed ({feedback.filter(f => f.feedback_type === 'downvote' || f.feedback_type === 'dismissed').length})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {filteredFeedback.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Sparkles className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground text-center mb-4">
                {activeTab === 'all' 
                  ? "No recommendation feedback yet. Visit the Discover page to get AI-powered recommendations!"
                  : `No ${activeTab === 'upvote' ? 'added' : 'dismissed'} recommendations yet.`}
              </p>
              <Button onClick={() => navigate('/discover')}>
                Discover Books
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredFeedback.map((item) => (
              <Card key={item.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <CardTitle className="text-lg mb-1">
                        {item.book_title}
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">
                        by {item.book_author}
                      </p>
                    </div>
                    <Badge 
                      variant={item.feedback_type === 'upvote' ? 'default' : 'destructive'}
                      className="flex items-center gap-1.5"
                    >
                      {getFeedbackIcon(item.feedback_type)}
                      {getFeedbackLabel(item.feedback_type)}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {item.recommendation_reason && (
                    <div className="bg-muted/50 rounded-lg p-3">
                      <div className="flex gap-2 mb-1">
                        <Sparkles className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <p className="text-xs font-medium">Why this was recommended</p>
                      </div>
                      <p className="text-sm text-muted-foreground ml-6">
                        {item.recommendation_reason}
                      </p>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      {format(new Date(item.created_at), 'MMM d, yyyy • h:mm a')}
                    </div>
                    
                    <div className="flex gap-2">
                      {item.book_id && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/book/${item.book_id}`)}
                        >
                          View Book
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteFeedback(item.id)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
